import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, Typography, Paper, List, ListItem,
  ListItemText, Chip, CircularProgress, Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PeopleIcon from '@mui/icons-material/People';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { dashboardAPI, smartCardAPI } from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  textAlign: 'center', padding: theme.spacing(2),
  cursor: 'pointer', transition: 'transform 0.2s',
  '&:hover': { transform: 'translateY(-4px)', boxShadow: theme.shadows[8] },
}));

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      dashboardAPI.stats(),
      smartCardAPI.list({ page: 1 }),
    ])
      .then(([statsRes, cardRes]) => {
        setStats(statsRes.data);
        setRecent(cardRes.data.results?.slice(0, 10) || []);
      })
      .catch((err) => console.error('AdminDashboard load error:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: <PeopleIcon />, color: '#9c27b0', path: '/admin/users' },
    { label: 'Total Smart Cards', value: stats?.total_smart_cards || 0, icon: <CreditCardIcon />, color: '#1976d2', path: '/admin/smart-cards' },
  ];

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Admin Dashboard</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        System overview and management
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={2.4} key={card.label}>
            <StyledCard onClick={() => navigate(card.path)}>
              <Box sx={{ color: card.color }}>{card.icon}</Box>
              <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{card.value}</Typography>
              <Typography variant="body2" color="text.secondary">{card.label}</Typography>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">Recent Smart Cards</Typography>
          <Button size="small" onClick={() => navigate('/admin/smart-cards')}>Manage All</Button>
        </Box>
        {recent.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>No smart cards yet</Typography>
        ) : (
          <List>
            {recent.map((card) => (
              <ListItem key={card.id} divider>
                <ListItemText
                  primary={`${card.card_id} - ${card.gemstone_name}`}
                  secondary={`${new Date(card.created_at).toLocaleDateString()}`}
                />
                <Chip label="Verified" size="small" color="success" />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
}
